import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/App';
import { formatCurrency, formatPercent, formatDate } from '@/lib/utils';
import { toast } from 'sonner';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow, TableFooter } from '@/components/ui/table';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Loader2 } from 'lucide-react';
import { 
  FileText, 
  Download, 
  Mail, 
  DollarSign, 
  Calendar, 
  ChevronDown, 
  ChevronRight,
  Printer,
  Users,
  FileSpreadsheet,
  CircleCheck,
  Circle
} from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import * as XLSX from 'xlsx';

const API = process.env.REACT_APP_BACKEND_URL;

// Pay period configurations
const PAY_PERIOD_TYPES = {
  weekly: { label: 'Weekly', days: 7 },
  biweekly: { label: 'Bi-Weekly', days: 14 },
  quarterly: { label: 'Quarterly', days: 91 },
  semiannual: { label: 'Semi-Annual', days: 182 },
  annually: { label: 'Annually', days: 365 }
};

// Base date for bi-weekly periods: December 27, 2025
const BIWEEKLY_BASE_START = new Date(2025, 11, 27); // Dec 27, 2025

// Generate pay periods based on type
function generatePayPeriods(type, year) {
  const periods = [];
  let startDate;
  let periodDays;
  
  if (type === 'biweekly') {
    // Use the base date and calculate periods
    // For bi-weekly, we need to generate periods both forward and backward from the base date
    let baseDate = new Date(BIWEEKLY_BASE_START);
    periodDays = 14;
    
    // First, go backwards to find the first period that starts on or before Jan 1 of the selected year
    startDate = new Date(baseDate);
    const yearStart = new Date(year, 0, 1);
    while (startDate > yearStart) {
      startDate.setDate(startDate.getDate() - periodDays);
    }
  } else if (type === 'weekly') {
    // Weekly starts on Jan 1
    startDate = new Date(year, 0, 1);
    periodDays = 7;
  } else if (type === 'quarterly') {
    // Quarterly: Jan-Mar, Apr-Jun, Jul-Sep, Oct-Dec
    const quarters = [
      { start: new Date(year, 0, 1), end: new Date(year, 2, 31) },
      { start: new Date(year, 3, 1), end: new Date(year, 5, 30) },
      { start: new Date(year, 6, 1), end: new Date(year, 8, 30) },
      { start: new Date(year, 9, 1), end: new Date(year, 11, 31) }
    ];
    quarters.forEach((q, idx) => {
      const statementDate = new Date(q.end);
      statementDate.setDate(statementDate.getDate() + 3);
      periods.push({
        periodNumber: idx + 1,
        periodLabel: `${year}-${idx + 1}`,
        startDate: q.start,
        endDate: q.end,
        statementDate: statementDate,
        label: `Q${idx + 1} ${year}`
      });
    });
    return periods;
  } else if (type === 'semiannual') {
    // Semi-annual: Jan-Jun, Jul-Dec
    const halves = [
      { start: new Date(year, 0, 1), end: new Date(year, 5, 30) },
      { start: new Date(year, 6, 1), end: new Date(year, 11, 31) }
    ];
    halves.forEach((h, idx) => {
      const statementDate = new Date(h.end);
      statementDate.setDate(statementDate.getDate() + 3);
      periods.push({
        periodNumber: idx + 1,
        periodLabel: `${year}-${idx + 1}`,
        startDate: h.start,
        endDate: h.end,
        statementDate: statementDate,
        label: `H${idx + 1} ${year}`
      });
    });
    return periods;
  } else if (type === 'annually') {
    const start = new Date(year, 0, 1);
    const end = new Date(year, 11, 31);
    const statementDate = new Date(end);
    statementDate.setDate(statementDate.getDate() + 3);
    periods.push({
      periodNumber: 1,
      periodLabel: `${year}-1`,
      startDate: start,
      endDate: end,
      statementDate: statementDate,
      label: `Year ${year}`
    });
    return periods;
  }
  
  // Generate weekly or bi-weekly periods
  let periodNum = 1;
  const yearEnd = new Date(year, 11, 31);
  
  while (startDate <= yearEnd) {
    const periodEnd = new Date(startDate);
    periodEnd.setDate(periodEnd.getDate() + periodDays - 1);
    
    // Only include periods that fall within or touch the selected year
    if (periodEnd.getFullYear() >= year && startDate.getFullYear() <= year) {
      const statementDate = new Date(periodEnd);
      statementDate.setDate(statementDate.getDate() + 3);
      
      const formatShortDate = (d) => {
        const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        return `${months[d.getMonth()]} ${d.getDate()}`;
      };
      
      periods.push({
        periodNumber: periodNum,
        periodLabel: `${year}-${periodNum}`,  // Year-prefixed statement number like "2025-1"
        startDate: new Date(startDate),
        endDate: periodEnd,
        statementDate: statementDate,
        label: `${formatShortDate(startDate)} - ${formatShortDate(periodEnd)}, ${periodEnd.getFullYear()}`
      });
      periodNum++;
    }
    
    startDate.setDate(startDate.getDate() + periodDays);
  }
  
  return periods;
}

// Generate statement number with year-prefixed period number
function generateStatementNumber(agentName, periodEnd, periodNum, periodLabel) {
  const initials = agentName.split(' ').map(n => n[0]).join('').toUpperCase();
  // Use periodLabel (e.g., "2025-1") if available, otherwise fallback to year-periodNum
  const periodStr = periodLabel || `${periodEnd.getFullYear()}-${periodNum}`;
  return `INS-${periodStr}-${initials}`;
}

export default function InsuranceStatements() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isAgent = user?.role === 'agent';
  
  // State
  const [commissions, setCommissions] = useState([]);
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedAgent, setSelectedAgent] = useState(isAgent ? user?.agent_id : '_all_');
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [periodType, setPeriodType] = useState('biweekly');
  const [expandedPeriods, setExpandedPeriods] = useState({});
  const [expandedCarriers, setExpandedCarriers] = useState({}); // Track expanded carriers within statements
  const [expandedAgents, setExpandedAgents] = useState({}); // Track expanded agents within periods
  const [downloadingPdf, setDownloadingPdf] = useState({}); // Track PDF download state per agent
  const [sendingEmail, setSendingEmail] = useState({}); // Track email sending state per agent
  const [searchTerm, setSearchTerm] = useState(''); // Search state
  const [activeTab, setActiveTab] = useState('generated'); // Tab state
  
  // Generate New Statement state
  const [genAgentId, setGenAgentId] = useState('');
  const [genStartDate, setGenStartDate] = useState(() => {
    const d = new Date(); d.setMonth(d.getMonth() - 1); d.setDate(1);
    return d.toISOString().split('T')[0];
  });
  const [genEndDate, setGenEndDate] = useState(new Date().toISOString().split('T')[0]);
  const [genStatement, setGenStatement] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [emailAddress, setEmailAddress] = useState('');
  
  const currentYear = new Date().getFullYear();
  const availableYears = Array.from({ length: 5 }, (_, i) => currentYear - i + 1);
  
  // Generate pay periods based on type and year
  const payPeriods = useMemo(() => {
    return generatePayPeriods(periodType, selectedYear);
  }, [periodType, selectedYear]);
  
  // Load data
  useEffect(() => {
    loadData();
  }, [selectedYear]);
  
  async function loadData() {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      
      // Load commissions
      const commResponse = await axios.get(`${API}/api/insurance/commissions/mga`, { headers });
      setCommissions(commResponse.data || []);
      
      // Load agents
      const agentsResponse = await axios.get(`${API}/api/insurance/agents`, { headers });
      setAgents(agentsResponse.data || []);
    } catch (err) {
      toast.error('Failed to load insurance data');
    } finally {
      setLoading(false);
    }
  }

  // Toggle paid status on a commission
  const handleMarkPaid = async (commissionId, currentPaid) => {
    const newPaid = !currentPaid;
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API}/api/insurance/commissions/mga/${commissionId}/mark-paid`,
        { paid: newPaid },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      // Update local state
      setCommissions(prev => prev.map(c => 
        c.id === commissionId ? { ...c, paid: newPaid, paid_date: newPaid ? new Date().toISOString() : null } : c
      ));
      toast.success(newPaid ? 'Marked as paid' : 'Marked as unpaid');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update paid status');
    }
  };

  // Generate statement by date range
  const handleInsGenerate = async () => {
    if (!genAgentId) { toast.error('Please select an agent'); return; }
    setGenerating(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API}/api/insurance/statements/generate`, {
        agent_id: genAgentId, start_date: genStartDate, end_date: genEndDate
      }, { headers: { Authorization: `Bearer ${token}` } });
      setGenStatement(res.data);
      const agent = agents.find(a => a.id === genAgentId);
      setEmailAddress(agent?.email || '');
      toast.success('Statement generated');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to generate statement');
    } finally { setGenerating(false); }
  };

  const handleInsDownloadPdf = async () => {
    if (!genAgentId || !genStatement) return;
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API}/api/insurance/statements/pdf`, {
        agent_id: genAgentId, start_date: genStartDate, end_date: genEndDate
      }, { headers: { Authorization: `Bearer ${token}` }, responseType: 'blob' });
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `insurance_statement_${genStatement.agent.name.replace(/\s/g, '_')}_${genStartDate}_${genEndDate}.pdf`;
      link.click();
      window.URL.revokeObjectURL(url);
      toast.success('PDF downloaded');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to download PDF');
    }
  };

  const handleInsSendEmail = async () => {
    if (!emailAddress) { toast.error('Please enter an email address'); return; }
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/api/insurance/statements/email`, {
        agent_id: genAgentId, start_date: genStartDate, end_date: genEndDate, email: emailAddress
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`Statement sent to ${emailAddress}`);
      setShowEmailModal(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send email');
    }
  };
  
  // Filter commissions by agent
  const filteredCommissions = useMemo(() => {
    let result = commissions;
    
    // Filter by agent
    if (selectedAgent !== '_all_') {
      result = result.filter(c => c.agent_id === selectedAgent);
    }
    
    // Filter by search term - search all commission data fields
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase();
      result = result.filter(c => 
        (c.agent_name && c.agent_name.toLowerCase().includes(term)) ||
        (c.carrier && c.carrier.toLowerCase().includes(term)) ||
        (c.insured_name && c.insured_name.toLowerCase().includes(term)) ||
        (c.contract_no && c.contract_no.toLowerCase().includes(term)) ||
        (c.coverage_name && c.coverage_name.toLowerCase().includes(term)) ||
        (c.transaction_type && c.transaction_type.toLowerCase().includes(term)) ||
        (c.category && c.category.toLowerCase().includes(term)) ||
        (c.memo && c.memo.toLowerCase().includes(term)) ||
        (c.agent_code && c.agent_code.toLowerCase().includes(term)) ||
        (c.transaction_date && c.transaction_date.includes(term)) ||
        (c.paid_to_agent_date && c.paid_to_agent_date.includes(term)) ||
        // Search numeric fields as strings
        (c.amount_due && c.amount_due.toString().includes(term)) ||
        (c.agent_comm && c.agent_comm.toString().includes(term)) ||
        (c.basis_amount && c.basis_amount.toString().includes(term))
      );
    }
    
    return result;
  }, [commissions, selectedAgent, searchTerm]);
  
  // Group commissions by pay period
  // Helper function to format date as YYYY-MM-DD
  const formatDateToYMD = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };
  
  const statementsByPeriod = useMemo(() => {
    const grouped = {};
    
    // Create a map of statement dates to periods for quick lookup
    // Include adjacent years to prevent cross-year duplicate assignment
    const statementDateToPeriod = {};
    
    // Map adjacent years' statement dates first (lower priority)
    [selectedYear - 1, selectedYear + 1].forEach(adjYear => {
      generatePayPeriods(periodType, adjYear).forEach(period => {
        const statementDateStr = formatDateToYMD(period.statementDate);
        statementDateToPeriod[statementDateStr] = `adj-${adjYear}-${period.periodNumber}`;
      });
    });
    
    // Map current year's periods (higher priority, overrides adjacent)
    payPeriods.forEach(period => {
      const statementDateStr = formatDateToYMD(period.statementDate);
      statementDateToPeriod[statementDateStr] = period.periodNumber;
    });
    
    // Track which commissions have been assigned to avoid duplicates
    const assignedCommissions = new Set();
    
    payPeriods.forEach(period => {
      const periodKey = `period-${period.periodNumber}`;
      
      // Get period start, end, and statement dates in YYYY-MM-DD format for comparison
      const periodStartStr = formatDateToYMD(period.startDate);
      const periodEndStr = formatDateToYMD(period.endDate);
      const statementDateStr = formatDateToYMD(period.statementDate);
      
      const periodCommissions = filteredCommissions.filter(c => {
        // Skip if already assigned to another period
        if (assignedCommissions.has(c.id)) return false;
        
        const dateStr = c.paid_to_agent_date;
        if (!dateStr) return false;
        
        // Normalize the date to YYYY-MM-DD format for comparison
        let normalizedDate;
        if (dateStr.includes('/')) {
          // MM/DD/YYYY format
          const [month, day, year] = dateStr.split('/');
          normalizedDate = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
        } else {
          // YYYY-MM-DD format - take first 10 chars in case of datetime
          normalizedDate = dateStr.substring(0, 10);
        }
        
        // Priority 1: Check if date equals THIS period's statement date
        if (normalizedDate === statementDateStr) {
          return true;
        }
        
        // Priority 2: Check if date matches ANY other period's statement date
        // If so, don't include it here (it belongs to that period)
        if (statementDateToPeriod[normalizedDate] !== undefined) {
          return false;
        }
        
        // Priority 3: Check if date falls within this pay period range
        return normalizedDate >= periodStartStr && normalizedDate <= periodEndStr;
      });
      
      // Mark these commissions as assigned
      periodCommissions.forEach(c => assignedCommissions.add(c.id));
      
      if (periodCommissions.length > 0) {
        // Group by agent within the period
        const byAgent = {};
        periodCommissions.forEach(c => {
          if (!byAgent[c.agent_id]) {
            const agent = agents.find(a => a.id === c.agent_id);
            byAgent[c.agent_id] = {
              agent_id: c.agent_id,
              agent_name: c.agent_name,
              agent_split: agent?.split_percentage || 100,
              transactions: [],
              totals: {
                carrier_paid: 0,
                mga_paid: 0,
                agent_commission: 0,
                net_to_brokerage: 0
              }
            };
          }
          
          // Use the agent_split_pct from the commission record (stored during import)
          // This is the actual split used when the commission was created
          const recordSplitPct = c.agent_split_pct || 100;
          
          // Use the pre-calculated agent_comm from the record
          // This was calculated correctly during import based on transaction type
          const commission = c.agent_comm || 0;
          const netToBrokerage = (c.amount_due || 0) - commission;
          
          byAgent[c.agent_id].transactions.push({
            ...c,
            agent_split: recordSplitPct,
            agent_commission: commission,
            net_to_brokerage: netToBrokerage,
            // Map fields for display
            carrier_paid: c.amount_due || 0,
            mga_paid: c.agent_comm || 0,
            plan_name: c.coverage_name || c.transaction_type,
            policy_number: c.contract_no,
            insured: c.insured_name
          });
          
          byAgent[c.agent_id].totals.carrier_paid += c.amount_due || 0;
          byAgent[c.agent_id].totals.mga_paid += c.agent_comm || 0;
          byAgent[c.agent_id].totals.agent_commission += commission;
          byAgent[c.agent_id].totals.net_to_brokerage += netToBrokerage;
        });
        
        grouped[periodKey] = {
          period,
          agentStatements: Object.values(byAgent),
          totalTransactions: periodCommissions.length,
          totalAmountDue: Object.values(byAgent).reduce((sum, a) => sum + a.totals.carrier_paid, 0),
          totalCommission: Object.values(byAgent).reduce((sum, a) => sum + a.totals.agent_commission, 0)
        };
      }
    });
    
    return grouped;
  }, [payPeriods, filteredCommissions, agents]);
  
  // Toggle period expansion
  const togglePeriod = (periodKey) => {
    setExpandedPeriods(prev => ({
      ...prev,
      [periodKey]: !prev[periodKey]
    }));
  };
  
  // Toggle carrier expansion within a statement
  const toggleCarrier = (statementKey, carrier) => {
    const key = `${statementKey}-${carrier}`;
    setExpandedCarriers(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };
  
  // Check if carrier is expanded
  const isCarrierExpanded = (statementKey, carrier) => {
    const key = `${statementKey}-${carrier}`;
    return expandedCarriers[key] || false;
  };
  
  // Group transactions by carrier
  const groupTransactionsByCarrier = (transactions) => {
    const grouped = {};
    transactions.forEach(trans => {
      const carrier = trans.carrier || 'Unknown Carrier';
      if (!grouped[carrier]) {
        grouped[carrier] = {
          carrier,
          transactions: [],
          totals: {
            basis_amount: 0,
            carrier_paid: 0,
            mga_paid: 0,
            agent_commission: 0,
            net_to_brokerage: 0
          }
        };
      }
      grouped[carrier].transactions.push(trans);
      grouped[carrier].totals.basis_amount += trans.basis_amount || 0;
      grouped[carrier].totals.carrier_paid += trans.carrier_paid || 0;
      grouped[carrier].totals.mga_paid += trans.mga_paid || 0;
      grouped[carrier].totals.agent_commission += trans.agent_commission || 0;
      grouped[carrier].totals.net_to_brokerage += trans.net_to_brokerage || 0;
    });
    
    // Sort carriers alphabetically
    return Object.values(grouped).sort((a, b) => a.carrier.localeCompare(b.carrier));
  };
  
  // Format date for display - handles YYYY-MM-DD strings correctly
  // Uses the shared formatDate from utils to ensure consistency with commissions page
  const formatDisplayDate = (dateStr) => {
    return formatDate(dateStr) || '-';
  };
  
  // Format date as YYYY-MM-DD for API
  const formatDateISO = (d) => {
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };
  
  // Download PDF for an agent's statement
  const handleDownloadPdf = async (agentId, agentName, period) => {
    const key = `${agentId}-${period.periodNumber}`;
    setDownloadingPdf(prev => ({ ...prev, [key]: true }));
    
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API}/api/insurance/statements/mga/pdf`,
        {
          agent_id: agentId,
          period_start: formatDateISO(period.startDate),
          period_end: formatDateISO(period.endDate),
          period_type: periodType,
          period_number: period.periodNumber
        },
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );
      
      // Create download link
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `MGA_Statement_${agentName.replace(/\s+/g, '_')}_${formatDateISO(period.startDate)}_${formatDateISO(period.endDate)}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success(`PDF downloaded for ${agentName}`);
    } catch (err) {
      // Check if it's a "no data" error
      if (err.response?.status === 404) {
        toast.error('No transactions found for this period');
      } else {
        toast.error(err.response?.data?.detail || 'Failed to download PDF');
      }
    } finally {
      setDownloadingPdf(prev => ({ ...prev, [key]: false }));
    }
  };
  
  // Email statement to an agent
  const handleEmailStatement = async (agentId, agentName, agentEmail, period) => {
    const key = `${agentId}-${period.periodNumber}`;
    setSendingEmail(prev => ({ ...prev, [key]: true }));
    
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API}/api/insurance/statements/mga/email`,
        {
          agent_id: agentId,
          period_start: formatDateISO(period.startDate),
          period_end: formatDateISO(period.endDate),
          period_type: periodType,
          period_number: period.periodNumber,
          email_to: agentEmail
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(`Statement emailed to ${response.data.email_to}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send email');
    } finally {
      setSendingEmail(prev => ({ ...prev, [key]: false }));
    }
  };

  // Download all visible statements as Excel
  const handleDownloadExcel = () => {
    const rows = [];
    Object.entries(statementsByPeriod).sort((a, b) => b[0].localeCompare(a[0])).forEach(([periodKey, periodData]) => {
      periodData.agentStatements.forEach(agentStmt => {
        agentStmt.transactions.forEach(trans => {
          rows.push({
            'Pay Period': periodData.period.label,
            'Statement Date': formatDateISO(periodData.period.statementDate),
            'Agent': agentStmt.agent_name,
            'Carrier': trans.carrier || '',
            'Category': trans.category || '',
            'Transaction Type': trans.transaction_type || '',
            'Insured': trans.insured_name || trans.insured || '',
            'Contract No': trans.contract_no || trans.policy_number || '',
            'Transaction Date': trans.transaction_date || '',
            'Paid to Agent Date': trans.paid_to_agent_date || '',
            'Basis Amount': trans.basis_amount || 0,
            'Comm %': trans.comm_pct || 0,
            'Amount Due': trans.amount_due || trans.carrier_paid || 0,
            'Agent Split %': trans.agent_split || 0,
            'Agent Commission': trans.agent_commission || 0,
            'Net to Brokerage': trans.net_to_brokerage || 0,
          });
        });
      });
    });
    if (rows.length === 0) { toast.error('No data to export'); return; }
    const ws = XLSX.utils.json_to_sheet(rows);
    ws['!cols'] = [
      { wch: 20 }, { wch: 14 }, { wch: 22 }, { wch: 20 }, { wch: 14 },
      { wch: 16 }, { wch: 22 }, { wch: 16 }, { wch: 14 }, { wch: 14 },
      { wch: 14 }, { wch: 10 }, { wch: 14 }, { wch: 12 }, { wch: 16 }, { wch: 16 }
    ];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Insurance Statements');
    XLSX.writeFile(wb, `Insurance_Statements_${selectedYear}.xlsx`);
    toast.success(`Downloaded ${rows.length} transactions`);
  };
  
  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-900"></div>
      </div>
    );
  }
  
  return (
    <div className="space-y-4 sm:space-y-6" data-testid="insurance-statements">
      {/* Header */}
      <div className="flex flex-col gap-2">
        <h2 className="text-xl sm:text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope' }}>
          Insurance Agent Statements
        </h2>
        <p className="text-sm text-slate-500">Commission statements by pay period</p>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="generated" className="flex items-center gap-2">
            <FileText className="w-4 h-4" />Generated Statements
          </TabsTrigger>
          <TabsTrigger value="generate-new" className="flex items-center gap-2">
            <FileSpreadsheet className="w-4 h-4" />Generate New Statement
          </TabsTrigger>
        </TabsList>

        {/* ===== TAB: Generate New Statement ===== */}
        <TabsContent value="generate-new" className="space-y-6 mt-4">
          <Card className="border-slate-200">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <FileSpreadsheet className="w-5 h-5 text-blue-600" />
                Generate Statement by Date Range
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-col sm:flex-row gap-4 items-end">
                {!isAgent && (
                  <div className="space-y-2 flex-1">
                    <Label>Agent</Label>
                    <Select value={genAgentId} onValueChange={setGenAgentId}>
                      <SelectTrigger data-testid="gen-agent-select">
                        <SelectValue placeholder="Select an agent" />
                      </SelectTrigger>
                      <SelectContent>
                        {agents.map(a => (
                          <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                <div className="space-y-2 flex-1">
                  <Label>Start Date</Label>
                  <Input type="date" value={genStartDate} onChange={e => setGenStartDate(e.target.value)} data-testid="gen-start-date" />
                </div>
                <div className="space-y-2 flex-1">
                  <Label>End Date</Label>
                  <Input type="date" value={genEndDate} onChange={e => setGenEndDate(e.target.value)} data-testid="gen-end-date" />
                </div>
                <Button onClick={handleInsGenerate} disabled={generating} className="bg-slate-900 hover:bg-slate-800" data-testid="gen-statement-btn">
                  {generating ? 'Generating...' : 'Generate'}
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Generated Statement Detail View */}
          {genStatement && (
            <Card className="border-slate-200">
              <CardContent className="p-6">
                {/* Statement Header */}
                <div className="flex justify-between items-start mb-6">
                  <div>
                    <h3 className="text-xl font-bold text-slate-900" style={{ fontFamily: 'Manrope' }}>Utmost Financial Inc.</h3>
                    <p className="text-sm text-slate-500">Insurance & Investment Division</p>
                  </div>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={handleInsDownloadPdf} data-testid="gen-download-pdf">
                      <Download className="w-4 h-4 mr-1" />PDF
                    </Button>
                    {!isAgent && (
                      <Button variant="outline" size="sm" onClick={() => setShowEmailModal(true)} data-testid="gen-email-btn">
                        <Mail className="w-4 h-4 mr-1" />Email
                      </Button>
                    )}
                  </div>
                </div>

                {/* Agent Info + Period */}
                <div className="grid grid-cols-2 gap-4 mb-6 p-4 bg-slate-50 rounded-lg">
                  <div>
                    <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">Agent</p>
                    <p className="font-semibold text-slate-900">{genStatement.agent.name}</p>
                    {genStatement.agent.email && <p className="text-sm text-slate-500">{genStatement.agent.email}</p>}
                  </div>
                  <div>
                    <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">Statement Period</p>
                    <p className="font-semibold text-slate-900">{formatDate(genStatement.period.start)} - {formatDate(genStatement.period.end)}</p>
                  </div>
                </div>

                {/* Summary Cards */}
                <div className="grid grid-cols-3 gap-4 mb-6">
                  <div className="bg-emerald-50 rounded-xl p-4 border border-emerald-100">
                    <p className="text-xs text-emerald-700 uppercase tracking-wider">Total Commissions</p>
                    <p className="text-xl font-bold font-mono text-emerald-700">{formatCurrency(genStatement.summary.total_commission)}</p>
                  </div>
                  <div className="bg-amber-50 rounded-xl p-4 border border-amber-100">
                    <p className="text-xs text-amber-700 uppercase tracking-wider">Less: Total Expenses</p>
                    <p className="text-xl font-bold font-mono text-amber-700">({formatCurrency(genStatement.summary.total_expenses)})</p>
                  </div>
                  <div className="bg-slate-900 rounded-xl p-4">
                    <p className="text-xs text-slate-300 uppercase tracking-wider">Net Commission</p>
                    <p className="text-xl font-bold font-mono text-white">{formatCurrency(genStatement.summary.net_commission)}</p>
                  </div>
                </div>

                {/* Commissions Table */}
                {genStatement.commissions.length > 0 && (
                  <div className="mb-6">
                    <h4 className="font-bold text-slate-900 mb-3">Commissions ({genStatement.commissions.length})</h4>
                    <div className="border border-slate-200 rounded-lg overflow-x-auto">
                      <Table>
                        <TableHeader>
                          <TableRow className="bg-slate-900">
                            <TableHead className="text-white text-xs">Date</TableHead>
                            <TableHead className="text-white text-xs">Carrier</TableHead>
                            <TableHead className="text-white text-xs">Category</TableHead>
                            <TableHead className="text-white text-xs">Insured</TableHead>
                            <TableHead className="text-white text-xs text-right">Basis Amt</TableHead>
                            <TableHead className="text-white text-xs text-right">Comm %</TableHead>
                            <TableHead className="text-white text-xs text-right">Amount Due</TableHead>
                            <TableHead className="text-white text-xs text-right">Agent Split</TableHead>
                            <TableHead className="bg-blue-600 text-white text-xs text-right font-bold">Agent Comm</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {genStatement.commissions.map((c, i) => {
                            const agentComm = c.agent_id === genAgentId ? c.agent_commission : c.co_agent_commission;
                            return (
                              <TableRow key={c.id || i} className="text-xs">
                                <TableCell className="font-mono whitespace-nowrap">{c.paid_to_agent_date || c.transaction_date || c.statement_date || '-'}</TableCell>
                                <TableCell>{c.carrier || '-'}</TableCell>
                                <TableCell>{c.category || '-'}</TableCell>
                                <TableCell>{c.insured_name || '-'}</TableCell>
                                <TableCell className="text-right font-mono">{formatCurrency(c.basis_amount || 0)}</TableCell>
                                <TableCell className="text-right font-mono">{c.comm_pct ? `${c.comm_pct}%` : '-'}</TableCell>
                                <TableCell className="text-right font-mono">{formatCurrency(c.amount_due || c.carrier_paid || 0)}</TableCell>
                                <TableCell className="text-right font-mono">{c.agent_split ? `${c.agent_split}%` : '-'}</TableCell>
                                <TableCell className="text-right font-mono font-bold bg-blue-50 text-blue-700">{formatCurrency(agentComm || 0)}</TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                        <TableFooter>
                          <TableRow className="bg-slate-100 font-bold text-xs">
                            <TableCell colSpan={8} className="text-right">Total Agent Commission:</TableCell>
                            <TableCell className="text-right font-mono text-emerald-700">{formatCurrency(genStatement.summary.total_commission)}</TableCell>
                          </TableRow>
                        </TableFooter>
                      </Table>
                    </div>
                  </div>
                )}

                {/* Expenses Table */}
                {genStatement.expenses && genStatement.expenses.length > 0 && (
                  <div>
                    <h4 className="font-bold text-slate-900 mb-3">Expenses ({genStatement.expenses.length})</h4>
                    <div className="border border-slate-200 rounded-lg overflow-x-auto">
                      <Table>
                        <TableHeader>
                          <TableRow className="bg-amber-600">
                            <TableHead className="text-white text-xs">Date</TableHead>
                            <TableHead className="text-white text-xs">Description</TableHead>
                            <TableHead className="text-white text-xs text-right">Amount</TableHead>
                            <TableHead className="text-white text-xs text-right">Tax</TableHead>
                            <TableHead className="text-white text-xs text-right">Total</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {genStatement.expenses.map((e, i) => (
                            <TableRow key={e.id || i} className="text-xs">
                              <TableCell className="font-mono">{e.expense_date || '-'}</TableCell>
                              <TableCell>{e.description || '-'}</TableCell>
                              <TableCell className="text-right font-mono">{formatCurrency(e.amount || 0)}</TableCell>
                              <TableCell className="text-right font-mono">{formatCurrency(e.tax || 0)}</TableCell>
                              <TableCell className="text-right font-mono font-bold">{formatCurrency(e.total_amount || 0)}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                        <TableFooter>
                          <TableRow className="bg-slate-100 font-bold text-xs">
                            <TableCell colSpan={4} className="text-right">Total Expenses:</TableCell>
                            <TableCell className="text-right font-mono text-amber-700">{formatCurrency(genStatement.summary.total_expenses)}</TableCell>
                          </TableRow>
                        </TableFooter>
                      </Table>
                    </div>
                  </div>
                )}

                {genStatement.commissions.length === 0 && (!genStatement.expenses || genStatement.expenses.length === 0) && (
                  <p className="text-center text-slate-500 py-8">No commissions or expenses found for this date range.</p>
                )}
              </CardContent>
            </Card>
          )}

          {/* Email Modal */}
          <Dialog open={showEmailModal} onOpenChange={setShowEmailModal}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Email Statement</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label>Email Address</Label>
                  <Input type="email" value={emailAddress} onChange={e => setEmailAddress(e.target.value)} placeholder="agent@example.com" data-testid="gen-email-input" />
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setShowEmailModal(false)}>Cancel</Button>
                <Button onClick={handleInsSendEmail}>Send Statement</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </TabsContent>

        {/* ===== TAB: Generated Statements (existing pay period view) ===== */}
        <TabsContent value="generated" className="space-y-4 sm:space-y-6 mt-4">
      
      {/* Filters */}
      <Card className="border-slate-200">
        <CardContent className="p-3 sm:p-4">
          <div className="grid grid-cols-2 sm:flex sm:flex-wrap gap-3 sm:gap-4 items-end">
            {/* Period Type */}
            <div className="space-y-1">
              <label className="text-xs font-medium text-slate-500">Pay Period</label>
              <Select value={periodType} onValueChange={setPeriodType}>
                <SelectTrigger className="w-full sm:w-[150px] touch-manipulation" data-testid="period-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(PAY_PERIOD_TYPES).map(([key, { label }]) => (
                    <SelectItem key={key} value={key}>{label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {/* Year */}
            <div className="space-y-1">
              <label className="text-xs font-medium text-slate-500">Year</label>
              <Select value={selectedYear.toString()} onValueChange={(v) => setSelectedYear(parseInt(v))}>
                <SelectTrigger className="w-full sm:w-[100px] touch-manipulation" data-testid="year-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {availableYears.map(year => (
                    <SelectItem key={year} value={year.toString()}>{year}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {/* Agent Filter */}
            {!isAgent && (
              <div className="space-y-1 col-span-2 sm:col-span-1">
                <label className="text-xs font-medium text-slate-500">Agent</label>
                <Select value={selectedAgent} onValueChange={setSelectedAgent}>
                  <SelectTrigger className="w-full sm:w-[200px] touch-manipulation" data-testid="agent-select">
                    <SelectValue placeholder="All Agents" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="_all_">All Agents</SelectItem>
                    {agents.map(agent => (
                      <SelectItem key={agent.id} value={agent.id}>{agent.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            
            {/* Search */}
            <div className="space-y-1 col-span-2 sm:col-span-1">
              <label className="text-xs font-medium text-slate-500">Search</label>
              <div className="relative">
                <input
                  type="text"
                  placeholder="Search all data..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full sm:w-[200px] h-10 sm:h-9 px-3 pl-8 text-sm border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                  data-testid="insurance-statement-search"
                />
                <svg className="absolute left-2.5 top-3 sm:top-2.5 h-4 w-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>
            </div>
            
            {/* Summary */}
            <div className="col-span-2 sm:col-span-1 sm:ml-auto flex items-center justify-between sm:justify-end gap-4 pt-2 sm:pt-0 border-t sm:border-t-0 border-slate-100">
              <Button
                variant="outline"
                size="sm"
                className="h-9 sm:h-8"
                onClick={handleDownloadExcel}
                disabled={Object.keys(statementsByPeriod).length === 0}
                data-testid="insurance-download-excel"
              >
                <FileSpreadsheet className="w-4 h-4 mr-1 text-emerald-600" />
                <span className="hidden sm:inline">Download Excel</span>
                <span className="sm:hidden">Excel</span>
              </Button>
              <div className="text-left sm:text-right">
                <p className="text-xs text-slate-500">Periods with Data</p>
                <p className="text-lg font-bold text-slate-900">{Object.keys(statementsByPeriod).length}</p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
      
      {/* Statements by Period */}
      <div className="space-y-3 sm:space-y-4">
        {payPeriods.map(period => {
          const periodKey = `period-${period.periodNumber}`;
          const periodData = statementsByPeriod[periodKey];
          const isExpanded = expandedPeriods[periodKey];
          const hasData = !!periodData;
          
          return (
            <Card 
              key={periodKey} 
              className={`border-slate-200 ${!hasData ? 'opacity-50' : ''}`}
              data-testid={`period-${period.periodNumber}`}
            >
              <Collapsible open={isExpanded} onOpenChange={() => hasData && togglePeriod(periodKey)}>
                <CollapsibleTrigger asChild>
                  <div 
                    className={`flex flex-col sm:flex-row sm:items-center justify-between p-3 sm:p-4 gap-2 sm:gap-0 ${hasData ? 'cursor-pointer hover:bg-slate-50 active:bg-slate-100' : 'cursor-not-allowed'} touch-manipulation`}
                  >
                    <div className="flex items-center gap-2 sm:gap-3">
                      {hasData ? (
                        isExpanded ? <ChevronDown className="w-5 h-5 text-slate-500 flex-shrink-0" /> : <ChevronRight className="w-5 h-5 text-slate-500 flex-shrink-0" />
                      ) : (
                        <ChevronRight className="w-5 h-5 text-slate-300 flex-shrink-0" />
                      )}
                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          <Calendar className="w-4 h-4 text-blue-500 flex-shrink-0 hidden sm:block" />
                          <span className="font-semibold text-slate-900 text-sm sm:text-base">{period.label}</span>
                        </div>
                        <p className="text-xs text-slate-500">
                          Statement: {formatDisplayDate(period.statementDate)}
                        </p>
                      </div>
                    </div>
                    
                    <div className="flex items-center justify-end gap-4 sm:gap-6 ml-7 sm:ml-0">
                      {hasData ? (
                        <>
                          <div className="text-right">
                            <p className="text-xs text-slate-500">Trans.</p>
                            <p className="font-semibold text-slate-900 text-sm sm:text-base">{periodData.totalTransactions}</p>
                          </div>
                          <div className="text-right">
                            <p className="text-xs text-slate-500">Amount Due</p>
                            <p className="font-semibold text-slate-700 text-sm sm:text-base">{formatCurrency(periodData.totalAmountDue)}</p>
                          </div>
                          <div className="text-right">
                            <p className="text-xs text-slate-500">Agent Comm</p>
                            <p className="font-semibold text-emerald-600 text-sm sm:text-base">{formatCurrency(periodData.totalCommission)}</p>
                          </div>
                        </>
                      ) : (
                        <span className="text-xs sm:text-sm text-slate-400">No transactions</span>
                      )}
                    </div>
                  </div>
                </CollapsibleTrigger>
                
                <CollapsibleContent>
                  {periodData && periodData.agentStatements.map(agentStatement => {
                    const statementNo = generateStatementNumber(
                      agentStatement.agent_name,
                      period.endDate,
                      period.periodNumber,
                      period.periodLabel
                    );
                    
                    const agentKey = `${periodKey}-${agentStatement.agent_id}`;
                    const isAgentExpanded = expandedAgents[agentKey];
                    
                    return (
                      <div 
                        key={agentStatement.agent_id} 
                        className="border-t border-slate-100"
                        data-testid={`statement-${agentStatement.agent_id}`}
                      >
                        {/* Agent Summary Header - Always visible */}
                        <div 
                          className="flex flex-col sm:flex-row sm:items-center justify-between p-3 sm:p-4 cursor-pointer hover:bg-slate-50 active:bg-slate-100 transition-colors touch-manipulation gap-3"
                          onClick={() => setExpandedAgents(prev => ({ ...prev, [agentKey]: !prev[agentKey] }))}
                        >
                          <div className="flex items-center gap-2 sm:gap-3">
                            {isAgentExpanded ? (
                              <ChevronDown className="w-5 h-5 text-slate-400 flex-shrink-0" />
                            ) : (
                              <ChevronRight className="w-5 h-5 text-slate-400 flex-shrink-0" />
                            )}
                            <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                              <Users className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600" />
                            </div>
                            <div className="min-w-0">
                              <h4 className="font-bold text-slate-900 text-sm sm:text-base truncate">{agentStatement.agent_name}</h4>
                              <p className="text-xs text-slate-500 truncate">
                                <span className="font-mono font-medium">{statementNo}</span>
                                {(() => {
                                  const paidCount = agentStatement.transactions.filter(t => t.paid).length;
                                  const totalCount = agentStatement.transactions.length;
                                  if (paidCount === totalCount) {
                                    return <span className="ml-2 inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-700 text-[10px] font-semibold"><CircleCheck className="w-3 h-3" />PAID</span>;
                                  } else if (paidCount > 0) {
                                    return <span className="ml-2 inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700 text-[10px] font-semibold">{paidCount}/{totalCount} Paid</span>;
                                  }
                                  return null;
                                })()}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center justify-between sm:justify-end gap-3 sm:gap-6 ml-7 sm:ml-0 flex-wrap">
                            <div className="flex items-center gap-4 sm:gap-6">
                              <div className="text-right">
                                <p className="text-xs text-slate-500">Trans.</p>
                                <p className="font-semibold text-sm">{agentStatement.transactions.length}</p>
                              </div>
                              <div className="text-right">
                                <p className="text-xs text-slate-500">Amount Due</p>
                                <p className="font-semibold text-slate-700 text-sm">{formatCurrency(agentStatement.totals.carrier_paid)}</p>
                              </div>
                              <div className="text-right">
                                <p className="text-xs text-slate-500">Net to Brokerage</p>
                                <p className="font-semibold text-blue-600 text-sm">{formatCurrency(agentStatement.totals.net_to_brokerage)}</p>
                              </div>
                              <div className="text-right">
                                <p className="text-xs text-slate-500">Agent Comm</p>
                                <p className="font-bold text-emerald-600 text-sm sm:text-base">{formatCurrency(agentStatement.totals.agent_commission)}</p>
                              </div>
                            </div>
                            <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                              <Button 
                                variant="outline" 
                                size="sm" 
                                className="h-9 sm:h-8 px-3 touch-manipulation"
                                onClick={() => handleDownloadPdf(agentStatement.agent_id, agentStatement.agent_name, period)}
                                disabled={downloadingPdf[`${agentStatement.agent_id}-${period.periodNumber}`]}
                              >
                                {downloadingPdf[`${agentStatement.agent_id}-${period.periodNumber}`] ? (
                                  <Loader2 className="w-4 h-4 sm:w-3 sm:h-3 animate-spin" />
                                ) : (
                                  <Download className="w-4 h-4 sm:w-3 sm:h-3" />
                                )}
                                <span className="hidden sm:inline ml-1">PDF</span>
                              </Button>
                              <Button 
                                variant="outline" 
                                size="sm" 
                                className="h-9 sm:h-8 px-3 touch-manipulation"
                                onClick={() => {
                                  const agent = agents.find(a => a.id === agentStatement.agent_id);
                                  handleEmailStatement(agentStatement.agent_id, agentStatement.agent_name, agent?.email, period);
                                }}
                                disabled={sendingEmail[`${agentStatement.agent_id}-${period.periodNumber}`]}
                              >
                                {sendingEmail[`${agentStatement.agent_id}-${period.periodNumber}`] ? (
                                  <Loader2 className="w-4 h-4 sm:w-3 sm:h-3 animate-spin" />
                                ) : (
                                  <Mail className="w-4 h-4 sm:w-3 sm:h-3" />
                                )}
                                <span className="hidden sm:inline ml-1">Email</span>
                              </Button>
                            </div>
                          </div>
                        </div>
                        
                        {/* Expandable Agent Details */}
                        {isAgentExpanded && (
                          <div className="px-3 sm:px-4 pb-3 sm:pb-4 pt-0">
                            {/* Statement Info */}
                            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4 mb-3 sm:mb-4 p-3 bg-slate-50 rounded-lg text-sm">
                              <div>
                                <p className="text-xs text-slate-500">Pay Period</p>
                                <p className="font-medium text-xs sm:text-sm">{period.label}</p>
                              </div>
                              <div>
                                <p className="text-xs text-slate-500">Statement Date</p>
                                <p className="font-medium text-xs sm:text-sm">{formatDisplayDate(period.statementDate)}</p>
                              </div>
                              <div>
                                <p className="text-xs text-slate-500">Net to Brokerage</p>
                                <p className="font-semibold text-blue-600">{formatCurrency(agentStatement.totals.net_to_brokerage)}</p>
                              </div>
                              <div>
                                <p className="text-xs text-slate-500">Agent Commission</p>
                                <p className="font-bold text-emerald-600">{formatCurrency(agentStatement.totals.agent_commission)}</p>
                              </div>
                            </div>
                            
                            {/* Transactions Grouped by Carrier */}
                            <div className="space-y-2">
                              {groupTransactionsByCarrier(agentStatement.transactions).map((carrierGroup, carrierIdx) => {
                                const statementKey = `${periodKey}-${agentStatement.agent_id}`;
                                const isCarrierOpen = isCarrierExpanded(statementKey, carrierGroup.carrier);
                                
                                return (
                                  <div key={carrierGroup.carrier} className="border border-slate-200 rounded-lg overflow-hidden">
                                    {/* Carrier Header - Collapsible */}
                                    <div 
                                      className="flex flex-col sm:flex-row sm:items-center justify-between p-3 bg-slate-50 cursor-pointer hover:bg-slate-100 active:bg-slate-150 transition-colors touch-manipulation gap-2"
                                      onClick={() => toggleCarrier(statementKey, carrierGroup.carrier)}
                                    >
                                      <div className="flex items-center gap-2">
                                        {isCarrierOpen ? (
                                          <ChevronDown className="w-4 h-4 text-slate-500 flex-shrink-0" />
                                        ) : (
                                          <ChevronRight className="w-4 h-4 text-slate-500 flex-shrink-0" />
                                        )}
                                        <span className="font-semibold text-slate-800 text-sm sm:text-base">{carrierGroup.carrier}</span>
                                        <span className="text-xs text-slate-500 bg-slate-200 px-2 py-0.5 rounded-full">
                                          {carrierGroup.transactions.length}
                                        </span>
                                      </div>
                                      <div className="flex items-center gap-4 sm:gap-6 text-xs sm:text-sm ml-6 sm:ml-0">
                                        <div className="text-right">
                                          <span className="text-xs text-slate-500 mr-1">Amt Due:</span>
                                          <span className="font-mono">{formatCurrency(carrierGroup.totals.carrier_paid)}</span>
                                        </div>
                                        <div className="text-right">
                                          <span className="text-xs text-slate-500 mr-1">Agent Comm:</span>
                                          <span className="font-mono font-medium text-emerald-600">{formatCurrency(carrierGroup.totals.agent_commission)}</span>
                                        </div>
                                      </div>
                                    </div>
                                    
                                    {/* Carrier Transactions Table - Expandable */}
                                    {isCarrierOpen && (
                                      <div className="overflow-x-auto -webkit-overflow-scrolling-touch">
                                        <Table className="min-w-[900px]">
                                          <TableHeader>
                                            <TableRow className="bg-white">
                                              <TableHead className="text-xs whitespace-nowrap">Date</TableHead>
                                              <TableHead className="text-xs whitespace-nowrap">Category</TableHead>
                                              <TableHead className="text-xs whitespace-nowrap">Type</TableHead>
                                              <TableHead className="text-xs whitespace-nowrap">Insured</TableHead>
                                              <TableHead className="text-xs text-right whitespace-nowrap">Basis Amt</TableHead>
                                              <TableHead className="text-xs text-right whitespace-nowrap">Comm %</TableHead>
                                              <TableHead className="text-xs text-right whitespace-nowrap">Amount Due</TableHead>
                                              <TableHead className="text-xs text-right whitespace-nowrap">Agent Split</TableHead>
                                              <TableHead className="text-xs text-right whitespace-nowrap">Agent Comm</TableHead>
                                              <TableHead className="text-xs text-center whitespace-nowrap">Status</TableHead>
                                            </TableRow>
                                          </TableHeader>
                                          <TableBody>
                                            {carrierGroup.transactions.map((trans, idx) => (
                                              <TableRow 
                                                key={trans.id || idx} 
                                                className="text-xs sm:text-sm cursor-pointer hover:bg-blue-50 transition-colors"
                                                onClick={() => {
                                                  if (trans.id) {
                                                    navigate(`/commissions?highlightId=${trans.id}`);
                                                  }
                                                }}
                                                title="Click to view in Commissions"
                                                data-testid={`statement-transaction-${trans.id}`}
                                              >
                                                <TableCell className="font-mono text-xs whitespace-nowrap">
                                                  {formatDisplayDate(trans.paid_to_agent_date || trans.transaction_date)}
                                                </TableCell>
                                                <TableCell className="whitespace-nowrap">
                                                  {trans.category || '-'}
                                                </TableCell>
                                                <TableCell className="whitespace-nowrap">
                                                  {trans.transaction_type || '-'}
                                                </TableCell>
                                                <TableCell className="max-w-[120px] truncate" title={trans.insured}>
                                                  {trans.insured || '-'}
                                                </TableCell>
                                                <TableCell className="text-right font-mono whitespace-nowrap">
                                                  {formatCurrency(trans.basis_amount || 0)}
                                                </TableCell>
                                                <TableCell className="text-right font-mono whitespace-nowrap">
                                                  {(trans.comm_pct || 0).toFixed(1)}%
                                                </TableCell>
                                                <TableCell className="text-right font-mono whitespace-nowrap">
                                                  {formatCurrency(trans.amount_due || trans.carrier_paid || 0)}
                                                </TableCell>
                                                <TableCell className="text-right font-mono whitespace-nowrap">
                                                  {trans.agent_split?.toFixed(0)}%
                                                </TableCell>
                                                <TableCell className="text-right font-mono font-medium text-emerald-600 whitespace-nowrap">
                                                  {formatCurrency(trans.agent_commission)}
                                                </TableCell>
                                                <TableCell className="text-center whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                                                  {!isAgent && (
                                                    <button
                                                      onClick={() => handleMarkPaid(trans.id, trans.paid)}
                                                      className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-[11px] font-semibold transition-colors ${
                                                        trans.paid 
                                                          ? 'bg-emerald-100 text-emerald-700 hover:bg-emerald-200' 
                                                          : 'bg-slate-100 text-slate-500 hover:bg-slate-200'
                                                      }`}
                                                      title={trans.paid ? `Paid${trans.paid_date ? ' on ' + formatDisplayDate(trans.paid_date) : ''}` : 'Click to mark as paid'}
                                                      data-testid={`mark-paid-${trans.id}`}
                                                    >
                                                      {trans.paid ? <CircleCheck className="w-3.5 h-3.5" /> : <Circle className="w-3.5 h-3.5" />}
                                                      {trans.paid ? 'Paid' : 'Unpaid'}
                                                    </button>
                                                  )}
                                                  {isAgent && (
                                                    <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-[11px] font-semibold ${
                                                      trans.paid ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-500'
                                                    }`}>
                                                      {trans.paid ? <><CircleCheck className="w-3.5 h-3.5" />Paid</> : <><Circle className="w-3.5 h-3.5" />Unpaid</>}
                                                    </span>
                                                  )}
                                                </TableCell>
                                              </TableRow>
                                            ))}
                                          </TableBody>
                                          <TableFooter>
                                            <TableRow className="bg-slate-100 font-medium">
                                              <TableCell colSpan={4} className="text-right text-xs">
                                                Carrier Subtotal:
                                              </TableCell>
                                              <TableCell className="text-right font-mono">
                                                {formatCurrency(carrierGroup.transactions.reduce((sum, t) => sum + (t.basis_amount || 0), 0))}
                                              </TableCell>
                                              <TableCell></TableCell>
                                              <TableCell className="text-right font-mono">
                                                {formatCurrency(carrierGroup.totals.carrier_paid)}
                                              </TableCell>
                                              <TableCell></TableCell>
                                              <TableCell className="text-right font-mono font-bold text-emerald-600">
                                                {formatCurrency(carrierGroup.totals.agent_commission)}
                                              </TableCell>
                                              <TableCell></TableCell>
                                            </TableRow>
                                          </TableFooter>
                                        </Table>
                                      </div>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                            
                            {/* Grand Total */}
                            <div className="mt-4 p-3 bg-emerald-50 rounded-lg border border-emerald-200">
                              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2">
                                <span className="font-semibold text-slate-700">Statement Total</span>
                                <div className="flex flex-wrap items-center gap-4 sm:gap-6">
                                  <div className="text-right">
                                    <span className="text-xs text-slate-500 mr-2">Amount Due:</span>
                                    <span className="font-mono font-medium">{formatCurrency(agentStatement.totals.carrier_paid)}</span>
                                  </div>
                                  <div className="text-right">
                                    <span className="text-xs text-blue-600 mr-2">Net to Brokerage:</span>
                                    <span className="font-mono font-semibold text-blue-600">{formatCurrency(agentStatement.totals.net_to_brokerage)}</span>
                                  </div>
                                  <div className="text-right">
                                    <span className="text-xs text-emerald-700 mr-2">Agent Comm:</span>
                                    <span className="font-mono font-bold text-emerald-700 text-lg">{formatCurrency(agentStatement.totals.agent_commission)}</span>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </CollapsibleContent>
              </Collapsible>
            </Card>
          );
        })}
        
        {Object.keys(statementsByPeriod).length === 0 && (
          <div className="text-center py-12 text-slate-500">
            <FileText className="w-12 h-12 mx-auto mb-4 text-slate-300" />
            <p className="font-medium">No statements found</p>
            <p className="text-sm">No commission transactions found for the selected period and filters.</p>
          </div>
        )}
      </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
